/**
 * Auth Context — GE Lines Self-Service Portal
 *
 * Uses the same Boox platform auth API (/api/selfservice/*).
 * Token storage key: `gelines_token` (isolated from other portals).
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useStorefront } from './StorefrontContext';
import { API_BASE } from '../lib/api-base';
import { normalizePortalRole, portalPermissionsForRole, type PortalPermission, type PortalRole } from '../lib/portal-access';

export interface PortalUser {
  id: string;
  email: string;
  name: string;
  customerId?: string;
  customerName?: string;
  role: PortalRole;
  portalPermissions?: PortalPermission[];
  relationshipRole?: string | null;
  tenantId: string;
  tenantName: string;
}

interface AuthContextType {
  user: PortalUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { tokenKey, legacyTokenKeys, host } = useStorefront();
  const readStoredToken = useCallback(() => {
    const direct = localStorage.getItem(tokenKey);
    if (direct) return direct;
    for (const legacyKey of legacyTokenKeys) {
      const legacy = localStorage.getItem(legacyKey);
      if (legacy) {
        localStorage.setItem(tokenKey, legacy);
        localStorage.removeItem(legacyKey);
        return legacy;
      }
    }
    return null;
  }, [legacyTokenKeys, tokenKey]);

  const [user, setUser] = useState<PortalUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(() => null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = !!user && !!accessToken;

  const fetchUser = useCallback(async (token: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/selfservice/me`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: AbortSignal.timeout(5000),
      });
      if (!res.ok) throw new Error('Session expired');
      const envelope = await res.json() as { ok: boolean; data?: { user: PortalUser } };
      if (!envelope.ok || !envelope.data?.user) throw new Error('Session expired');
      setUser({
        ...envelope.data.user,
        role: normalizePortalRole(envelope.data.user.role),
        portalPermissions: envelope.data.user.portalPermissions ?? portalPermissionsForRole(envelope.data.user.role),
      });
      return true;
    } catch {
      setUser(null);
      setAccessToken(null);
      localStorage.removeItem(tokenKey);
      return false;
    }
  }, [tokenKey]);

  useEffect(() => {
    const init = async () => {
      const stored = readStoredToken();
      setAccessToken(stored);
      setIsLoading(!!stored);
      if (stored) await fetchUser(stored);
      setIsLoading(false);
    };
    void init();
  }, [fetchUser, readStoredToken, tokenKey]);

  const login = async (email: string, password: string) => {
    setError(null);
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/storefront/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ host, email, password }),
      });
      const envelope = await res.json() as {
        ok: boolean;
        data?: { accessToken: string; user: PortalUser };
        error?: { code: string; message: string };
      };
      if (!res.ok || !envelope.ok) {
        throw new Error(envelope.error?.message ?? 'Login failed');
      }
      const { accessToken: token, user: userData } = envelope.data!;
      setAccessToken(token);
      localStorage.setItem(tokenKey, token);
      setUser({
        ...userData,
        role: normalizePortalRole(userData.role),
        portalPermissions: userData.portalPermissions ?? portalPermissionsForRole(userData.role),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      if (accessToken) {
        await fetch(`${API_BASE}/api/selfservice/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
        });
      }
    } finally {
      setUser(null);
      setAccessToken(null);
      localStorage.removeItem(tokenKey);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, accessToken, isAuthenticated, isLoading, error, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
